import { useEffect, useSyncExternalStore } from "react";

import { type Audience, AUDIENCES } from "@/5-shared/types/common";

export type { Audience };

const KEY = "dec-audience";
const DEFAULT: Audience = "companies";

function audienceFromRoute(route: string): Audience | null {
  if (route.startsWith("/producer")) return "producers";
  if (route.startsWith("/company")) return "companies";
  return null;
}

function readStored(): Audience | null {
  try {
    const v = localStorage.getItem(KEY);
    return AUDIENCES.find((a) => a === v) ?? null;
  } catch {
    return null;
  }
}

/**
 * Mirror "has stored audience" onto `<html data-known>`, the attribute the nav
 * CSS keys the cold/full desktop navbar off. The pre-paint probe in `+Head`
 * sets it on hard loads; this keeps it in sync across SPA client navigation
 * (e.g. the landing fork writing an audience then `navigate()`-ing away), so
 * the full navbar shows without a refresh.
 */
function syncKnownAttr(known: boolean) {
  if (typeof document === "undefined") return;
  if (known) document.documentElement.setAttribute("data-known", "1");
  else document.documentElement.removeAttribute("data-known");
}

let snapshot: Audience | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

function getSnapshot(): Audience {
  if (snapshot === null) snapshot = readStored() ?? DEFAULT;
  return snapshot;
}

function getServerSnapshot(): Audience {
  return DEFAULT;
}

function subscribe(fn: () => void): () => void {
  if (listeners.size === 0 && typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
}

function onStorage(e: StorageEvent) {
  if (e.key !== null && e.key !== KEY) return;
  const stored = readStored();
  snapshot = stored ?? DEFAULT;
  syncKnownAttr(stored !== null);
  emit();
}

export function setStoredAudience(a: Audience) {
  try {
    localStorage.setItem(KEY, a);
  } catch {
    /* private mode, session-only is fine */
  }
  snapshot = a;
  syncKnownAttr(true);
  emit();
}

/**
 * Whether an audience is persisted for this visitor, i.e. they have already been
 * through the company/producer funnel on this device. Distinct from
 * `useAudience`, which falls back to `DEFAULT` and so cannot tell "never set"
 * from "companies". SSR/first paint reports false (no storage), the client
 * reconciles after hydration.
 */
export function useHasStoredAudience(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => readStored() !== null,
    () => false,
  );
}

export function useAudience(route: string): Audience {
  const stored = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const derived = audienceFromRoute(route);
  useEffect(() => {
    if (derived) setStoredAudience(derived);
  }, [derived]);
  return derived ?? stored;
}
